import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { type SubscriptionPeriod } from "@/repositories/interfaces";
import { repositoryFactory } from "@/repositories/RepositoryFactory";

export const useSubscriptionPeriods = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const subscriptionPeriodRepo =
    repositoryFactory.getSubscriptionPeriodRepository();
  const queryKey = ["subscriptionPeriods", user?.id];

  const {
    data: subscriptionPeriods = [],
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => subscriptionPeriodRepo.getAll(),
    enabled: !!user,
  });

  const createSubscriptionPeriod = useMutation({
    mutationKey: ["createSubscriptionPeriod"],
    mutationFn: (
      period: Omit<SubscriptionPeriod, "id" | "created_at" | "updated_at">,
    ) => subscriptionPeriodRepo.create(period),
    onMutate: async (newItem) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<SubscriptionPeriod[]>(queryKey);
      queryClient.setQueryData<SubscriptionPeriod[]>(queryKey, (old = []) => [
        ...old,
        {
          ...newItem,
          id: crypto.randomUUID(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as SubscriptionPeriod,
      ]);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateSubscriptionPeriod = useMutation({
    mutationKey: ["updateSubscriptionPeriod"],
    mutationFn: ({
      id,
      ...updates
    }: Partial<SubscriptionPeriod> & { id: string }) =>
      subscriptionPeriodRepo.update(id, updates),
    onMutate: async ({ id, ...updates }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<SubscriptionPeriod[]>(queryKey);
      queryClient.setQueryData<SubscriptionPeriod[]>(queryKey, (old = []) =>
        old.map((p) =>
          p.id === id
            ? { ...p, ...updates, updated_at: new Date().toISOString() }
            : p,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteSubscriptionPeriod = useMutation({
    mutationKey: ["deleteSubscriptionPeriod"],
    mutationFn: (id: string) => subscriptionPeriodRepo.delete(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<SubscriptionPeriod[]>(queryKey);
      queryClient.setQueryData<SubscriptionPeriod[]>(queryKey, (old = []) =>
        old.filter((p) => p.id !== id),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const getPeriodsBySubscription = (subscriptionId: string) => {
    return subscriptionPeriods.filter(
      (period) => period.subscription_id === subscriptionId,
    );
  };

  return {
    subscriptionPeriods,
    isLoading,
    error,
    createSubscriptionPeriod,
    updateSubscriptionPeriod,
    deleteSubscriptionPeriod,
    getPeriodsBySubscription,
  };
};
